import { useState } from 'react';
import { Star, Send, User, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { reviewsAPI } from '../../lib/api';
import { toast } from 'sonner';

export const ReviewWidget = ({ doctorId, doctorName }) => {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [formData, setFormData] = useState({
    rating: 5,
    patient_name: '',
    patient_email: '',
    title: '',
    review: '',
    treatment: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.patient_name || !formData.title || !formData.review) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await reviewsAPI.create({
        doctor_id: doctorId,
        ...formData
      });
      setSubmitted(true);
      toast.success('Thank you! Your review has been submitted for approval.');
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-green-800 mb-2">Thank You!</h3>
          <p className="text-green-700 text-sm">
            Your review for Dr. {doctorName} has been submitted and will be visible after approval.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!showForm) {
    return (
      <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setShowForm(true)}>
        <CardContent className="pt-6 text-center">
          <div className="w-14 h-14 bg-[#c5a065]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Star className="w-7 h-7 text-[#c5a065]" />
          </div>
          <h3 className="text-lg font-semibold text-[#23265d] mb-1">Rate Your Experience</h3>
          <p className="text-gray-500 text-sm mb-4">
            Had a consultation with Dr. {doctorName}? Share your feedback!
          </p>
          <Button className="bg-[#23265d] hover:bg-[#1e1e4b]" data-testid="write-review-btn">
            Write a Review
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Write a Review</CardTitle>
        <CardDescription>Share your experience with Dr. {doctorName}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Star Rating */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Your Rating *</label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star 
                    className={`w-8 h-8 ${
                      star <= (hoveredRating || formData.rating) 
                        ? 'text-amber-400 fill-current' 
                        : 'text-gray-200'
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-500">
                {formData.rating === 5 ? 'Excellent' : 
                 formData.rating === 4 ? 'Very Good' : 
                 formData.rating === 3 ? 'Good' : 
                 formData.rating === 2 ? 'Fair' : 'Poor'}
              </span>
            </div>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Your Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={formData.patient_name}
                onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                placeholder="John Doe"
                className="pl-10"
                data-testid="review-name"
              />
            </div>
          </div>

          {/* Email (optional) */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Email <span className="text-gray-400">(optional)</span></label>
            <Input
              type="email"
              value={formData.patient_email}
              onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
              placeholder="john@example.com"
              data-testid="review-email"
            />
          </div>

          {/* Treatment */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Treatment Received <span className="text-gray-400">(optional)</span></label>
            <Input
              value={formData.treatment}
              onChange={(e) => setFormData({ ...formData, treatment: e.target.value })}
              placeholder="e.g., Acne Treatment, Hair Transplant"
              data-testid="review-treatment"
            />
          </div>

          {/* Review Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Review Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Summarize your experience"
              data-testid="review-title"
            />
          </div>

          {/* Review Text */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Your Review *</label>
            <Textarea
              value={formData.review}
              onChange={(e) => setFormData({ ...formData, review: e.target.value })}
              placeholder="Share details about your experience with the doctor, treatment, staff, etc."
              rows={4}
              data-testid="review-text"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#23265d] hover:bg-[#1e1e4b] gap-2"
              data-testid="submit-review-btn"
            >
              {loading ? 'Submitting...' : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Review
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
